import { render, h, Fragment } from "preact";
import { useState } from "preact/hooks";
import useFormStore from "../../../store/useFormStore";
import { Counter, InfoIcon } from "./ConfigServicesComponents";
import ServiceStatusBadge from "./ServiceStatusBadge";

// Helper to get column key from label (normalize to lowercase with underscores).
const getColumnKey = (label) => {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

// Accordion row component for mobile - defined outside to maintain stable identity across renders
const AccordionRow = ({
  row,
  rowIndex,
  isLastRow,
  columns,
  data,
  updateValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <tr
      className={`block @3xl:table-row mb-1 @3xl:mb-0 bg-gray-50 ${rowIndex % 2 !== 0 ? "@3xl:bg-white" : ""}`}
    >
      {/* Row Label - clickable accordion header on mobile */}
      <td
        className={`block @3xl:table-cell p-3 font-bold text-lg @3xl:text-base @3xl:font-medium border-gray-500 @3xl:border-b bg-gray-200 @3xl:bg-transparent ${!isLastRow ? "@3xl:border-b-0" : ""}`}
      >
        <button
          type="button"
          className="flex w-full normal-case text-base @3xl:font-medium items-center justify-between text-left @3xl:pointer-events-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{row.label}</span>
          <svg
            className={`h-5 w-5 text-black transition-transform @3xl:hidden ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </td>

      {/* Column cells - hidden on mobile when collapsed */}
      {columns.map((column, colIndex) => {
        const columnKey = getColumnKey(column.label);
        return (
          <td
            key={colIndex}
            data-label={column.label}
            className={`${isOpen ? "flex" : "hidden"} @3xl:table-cell justify-between items-center p-3 @3xl:p-2 border-gray-500 before:content-[attr(data-label)] before:font-semibold before:block @3xl:before:hidden`}
          >
            {column.type === "counter" ? (
              <div className="flex flex-col items-center gap-1">
                <Counter
                  value={data[row.id]?.[columnKey] || 0}
                  onChange={(value) => updateValue(data, row, columnKey, value)}
                  min={row.min ? row.min : 0}
                  label={`${row.label} ${column.label}`}
                />
              </div>
            ) : (
              <div className="text-center text-gray-400">—</div>
            )}
          </td>
        );
      })}
    </tr>
  );
};

// Reusable Service Detail Component
export default function ConfigureServicesDetails({
  serviceId,
  serviceConfig,
  isConfigured,
  isRequired,
}) {
  // Get data from store for this specific service
  const selectedServicesConfiguration = useFormStore(
    (state) => state.selectedServicesConfiguration,
  );
  const updateServiceConfiguration = useFormStore(
    (state) => state.updateServiceConfiguration,
  );

  const data = selectedServicesConfiguration[serviceId] || {};

  const updateValue = (data, row, columnId, value) => {
    const rowId = row.id;

    // Get the first column of the row
    const firstColumn = Object.keys(row.prices)[0];

    // Edge case 1: If first column is set to 0, reset all other columns to 0
    if (columnId === firstColumn && value === 0) {
      // Get all column keys from the actual data state (not row.prices)
      // This ensures we use the normalized keys like "sanitize__protect" not "sanitize"
      const currentRowData = data[rowId] || {};
      const allColumnKeys = Object.keys(currentRowData);

      // Reset all columns (including the first one) to 0
      allColumnKeys.forEach((col) => {
        updateServiceConfiguration(serviceId, rowId, col, 0);
      });
      return; // Exit early since we've handled all updates
    }

    // Edge case 2: If setting an addon column > 0 and first column is 0, auto-set first column
    if (
      value > 0 &&
      firstColumn !== columnId &&
      (data?.[rowId]?.[firstColumn] === undefined ||
        data[rowId][firstColumn] == 0)
    ) {
      updateServiceConfiguration(serviceId, rowId, firstColumn, row?.min || 1);
    }

    // Update the target column
    updateServiceConfiguration(serviceId, rowId, columnId, value);
  };

  const ServiceTable = (serviceConfig) => {
    return (
      <table className="w-full border-collapse mb-0 text-base min-w-full service-table">
        <thead className="hidden @3xl:table-header-group">
          <tr>
            <th className="text-left py-3 px-4 font-semibold">
              <span className="sr-only">Service options</span>
            </th>
            {serviceConfig.columns.map((column, index) => (
              <th
                key={index}
                className="text-center py-3 px-4 font-semibold border border-gray-500 border-b-0"
              >
                <div className="flex items-center justify-center gap-1">
                  <span>{column.label}</span>
                  {column.hasInfo && column.info && (
                    <div className="relative">
                      <InfoIcon
                        id={`service-${column.id}`}
                        message={column.info}
                      />
                    </div>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="block @3xl:table-row-group">
          {serviceConfig.rows.map((row, rowIndex) => {
            const isLastRow = rowIndex === serviceConfig.rows.length - 1;
            return (
              <AccordionRow
                key={row.id}
                row={row}
                rowIndex={rowIndex}
                isLastRow={isLastRow}
                columns={serviceConfig.columns}
                data={data}
                updateValue={updateValue}
              />
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div
      className="mb-8 border-b border-b-gray-300 pb-6 scroll-mt-48"
      id={`service-${serviceConfig.title.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-1">
        <div className="text-xl font-semibold">{serviceConfig.title}</div>
        {serviceConfig.type === "table" && (
          <ServiceStatusBadge
            isConfigured={isConfigured}
            isRequired={isRequired}
          />
        )}
      </div>

      {/* Content */}
      <div className="@container">
        {serviceConfig.type === "table" && ServiceTable(serviceConfig)}
        {serviceConfig.type === "message" && (
          <div dangerouslySetInnerHTML={{ __html: serviceConfig.message }} />
        )}

        {/* Footer Information */}
        {serviceConfig.footerInfo && (
          <div className="mt-2">
            <div
              className="text-sm text-gray-700"
              dangerouslySetInnerHTML={{ __html: serviceConfig.footerInfo }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
